import {checkIfInfantryUnitType, UNIT_TYPES} from "../../../configs/units";
import {
  UNIT_D4_SIZE_FOR_SUPPLY_ON_BARRACKS,
  UNIT_D4_SIZE_FOR_SUPPLY_ON_OTHER,
  UNIT_D4_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP,
  UNIT_SIZE_DECREASE_WITHOUT_SUPPLY,
  UNIT_SIZE_FOR_SUPPLY_ON_BARRACKS,
  UNIT_SIZE_FOR_SUPPLY_ON_OTHER,
  UNIT_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP
} from "../../../constants/units";
import {isUnitFaction} from "../../factions/isUnitFaction";
import {FACTIONS} from "../../../constants/factions";
import {ELVES_SIZE_FOR_SUPPLY, ELVES_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP} from "../../../configs/factions/elves";
import {
  BEASTMEN_SIZE_FOR_SUPPLY_ON_BARRACKS,
  BEASTMEN_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP
} from "../../../configs/factions/beastmen";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {POLICY_BARBARISM_UNIT_SIZE_DECREASE_WITHOUT_SUPPLY} from "../../../configs/policy/barbarism";
import {RATMEN_SIZE_FOR_ADDITIONAL_SUPPLY} from "../../../configs/factions/ratmen";
import {getPlayerSupplySizeBonus} from "../../../configs/botLeagueSupplyGrowth";

export const calcUnitUpdateBasedOnPendingSupply = (unit, owner, pendingSupply) => {
  const supplyActions = pendingSupply.filter(s => s.unitId === unit.id);
  const unitUpdate = {
    sizeUpdate: 0,
    additionalSupply: false,
  };

  if (supplyActions.length > 0) {
    // Бонус военного лагеря (+4 вместо +2 на казарме) действует один раз за ход -
    // на первое полученное базовое снабжение. Обычные фракции получают не более
    // 1 базового снабжения за ход, так что для них это не меняет поведение, но
    // великаны могут получать базовое снабжение неограниченно - без этого флага
    // лагерь бустил бы КАЖДОЕ их базовое снабжение, а не только первое
    let militaryCampBonusUsed = false;
    // Прибавка ботов старших лиг к росту (см. configs/botLeagueSupplyGrowth) —
    // тоже один раз за ход, на первое базовое снабжение: великанам остальные
    // базовые снабжения дают стандартный рост
    let botSupplySizeBonusUsed = false;

    for (const supplyAction of supplyActions) {
      const {
        isOnWorkingBarracks,
        additionalSupply,
        isAdjacentToWorkingMilitaryCamp,
        isOnEmptyHex
      } = supplyAction
      // Если было дополнительное снабжение - устанавливаем флаг
      if (additionalSupply) {
        // Отряд получил дополнительное (второе) снабжение - устанавливаем для отряда соответвующий флаг,
        // Но не меняем размер отряда
        unitUpdate.additionalSupply = true;

        // Крысолюды: прирост размера за дополнительное снабжение получает
        // только пехота (d6/d10/d20) — катапультам и кавалерии он не достается
        if (isUnitFaction(unit, FACTIONS.RATMEN) && checkIfInfantryUnitType(unit.type)) {
          const sizeToMax = unit.maxSize - (unit.size + unitUpdate.sizeUpdate);
          unitUpdate.sizeUpdate += Math.min(sizeToMax, RATMEN_SIZE_FOR_ADDITIONAL_SUPPLY)
        }
      } else {
        // Проверяем наличие military_camp рядом с казармой (только для первого
        // базового снабжения за ход - см. militaryCampBonusUsed выше)
        const applyMilitaryCampBonus = isAdjacentToWorkingMilitaryCamp && !militaryCampBonusUsed;

        let supplySize;
        if (unit.type === UNIT_TYPES.D4.id) {
          if (isOnWorkingBarracks) {
            supplySize = applyMilitaryCampBonus ?
              UNIT_D4_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP : UNIT_D4_SIZE_FOR_SUPPLY_ON_BARRACKS;
          } else {
            supplySize = UNIT_D4_SIZE_FOR_SUPPLY_ON_OTHER;
          }
        } else {
          // Снабжение эльфов
          if (isUnitFaction(unit, FACTIONS.ELVES)) {
            if (isOnWorkingBarracks && applyMilitaryCampBonus) {
              supplySize = ELVES_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP;
            } else {
              supplySize = ELVES_SIZE_FOR_SUPPLY;
            }
          } else if (isOnWorkingBarracks) {
            // Снабжение зверолюдей
            if (isUnitFaction(unit, FACTIONS.BEASTMEN)) {
              supplySize = applyMilitaryCampBonus ?
                BEASTMEN_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP : BEASTMEN_SIZE_FOR_SUPPLY_ON_BARRACKS;
            } else {
              supplySize = applyMilitaryCampBonus ?
                UNIT_SIZE_FOR_SUPPLY_WITH_MILITARY_CAMP : UNIT_SIZE_FOR_SUPPLY_ON_BARRACKS;
            }
          } else {
            supplySize = UNIT_SIZE_FOR_SUPPLY_ON_OTHER;
          }
        }

        if (isOnWorkingBarracks && isAdjacentToWorkingMilitaryCamp) {
          militaryCampBonusUsed = true;
        }

        // Снабжение инсектоидов
        if (isUnitFaction(unit, FACTIONS.INSECTOIDS) && isOnEmptyHex) {
          supplySize = 0;
        }

        // Прибавка ложится только на рост: где отряд за снабжение не растет
        // (d4 вне казармы, инсектоиды на пустой ячейке), не растет и с ней
        if (!botSupplySizeBonusUsed && supplySize > 0) {
          supplySize += getPlayerSupplySizeBonus(owner, isOnWorkingBarracks);
          botSupplySizeBonusUsed = true;
        }

        const sizeToMax = unit.maxSize - (unit.size + unitUpdate.sizeUpdate);

        unitUpdate.sizeUpdate += Math.min(sizeToMax, supplySize)
      }
    }
  } else {
    let decreaseWithoutSupply = UNIT_SIZE_DECREASE_WITHOUT_SUPPLY;

    if (isPlayerPolicy(owner, POLICIES.BARBARISM)) {
      decreaseWithoutSupply = POLICY_BARBARISM_UNIT_SIZE_DECREASE_WITHOUT_SUPPLY;
    }

    // Отряд не получил снабжение - уменьшаем размер
    unitUpdate.sizeUpdate = Math.max(-unit.size, decreaseWithoutSupply);
  }

  return unitUpdate;
}